import React, { useState, useEffect } from "react";
import "../css/UserEditModal.css";
import { useAuthProvider } from '../../providers/AuthProvider';

const moneyRegex = /^\d+(\.\d{2})$/;
const costFields = [
    "weekly_pp", "handling", "rhd", "wrap", "band", "pick",
    "supply_pallet", "twenty_ft_handball", "forty_ft_handball",
];
const pallettrackServiceFields = [
    { code: "EC", label: "Economy" },
    { code: "ND", label: "Next Day" },
    { code: "EP", label: "Economy Pallet-Plus" },
    { code: "NP", label: "Next Day Pallet-Plus" },
];
const pallettrackSurchargeFields = [
    ["AM", "AM Weekday"], ["AZ", "Amazon Booking"], ["BI", "Book In"],
    ["CC", "EU Customer Charges"], ["EA", "EU AM Delivery"],
    ["EL", "EU Tail Lift Delivery"], ["EN", "NES"],
    ["EP", "EU Pre-Booked Delivery"], ["ES", "EU Proof of Shipment"],
    ["EX", "EX1"], ["FD", "FORS Delivery"], ["HD", "Home Delivery"],
    ["LF", "Tail Lift Delivery Required"], ["LQ", "Limited Quantity"],
    ["PB", "Pre-Booked"], ["PM", "PM Weekday"],
    ["PS", "Premium Time Specific Delivery"], ["PT", "Premium Timed Delivery"],
    ["SA", "Saturday AM"], ["SP", "Saturday PM"], ["ST", "Saturday Timed"],
    ["SZ", "Amazon Saturday Booking"], ["TI", "Timed Delivery"],
    ["TS", "Time Specific"], ["10", "Pre-10 Delivery"], ["VR", "Van Request"],
].map(([code, label]) => ({ code, label }));
const moneyValue = (value) => Number(value || 0).toFixed(2);
const pallettrackPriceState = (costs) => {
    const legacyServices = { EC: costs?.surcharge_economy, ND: costs?.surcharge_next_day };
    const legacySurcharges = {
        AM: costs?.surcharge_am, AZ: costs?.surcharge_amazon_book_in,
        BI: costs?.surcharge_book_in, LF: costs?.surcharge_tail_lift,
        SA: costs?.surcharge_saturday_am, "10": costs?.surcharge_pre_10_am,
    };
    return {
        pallet_price: moneyValue(costs?.pallet_price),
        pallettrack_service_prices: Object.fromEntries(pallettrackServiceFields.map(({ code }) => [
            code, moneyValue(costs?.pallettrack_service_prices?.[code] ?? legacyServices[code]),
        ])),
        pallettrack_surcharge_prices: Object.fromEntries(pallettrackSurchargeFields.map(({ code }) => [
            code, moneyValue(costs?.pallettrack_surcharge_prices?.[code] ?? legacySurcharges[code]),
        ])),
    };
};

const CustomerEditModal = ({
    theme,
    customer,
    selectedCosts,
    selectedPalletCosts,
    isOpen,
    onClose,
    onSubmit,
}) => {

    const { features } = useAuthProvider();
    const warehouseEnabled = features?.warehouse !== false;

    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        id: customer.id || "",
        company: customer.company || "",
        address: customer.address || "",
        postcode: customer.postcode || "",
        email: customer.email || "",
        phone: customer.phone || "",
        account_ref: customer.account_ref || "",
        payment_terms: customer.payment_terms || "monthly",
        fuel_surcharge: customer.fuel_surcharge || null,
        has_storage: Array.isArray(selectedCosts) && selectedCosts.length > 0,
        has_pallet_network: Boolean(selectedPalletCosts),
        weekly_pp: selectedCosts?.[0]?.weekly_pp || "",
        handling: selectedCosts?.[0]?.handling || "",
        use_rhd: selectedCosts?.[0]?.use_rhd || false,
        rhd: selectedCosts?.[0]?.rhd || "",
        wrap: selectedCosts?.[0]?.wrap || "",
        band: selectedCosts?.[0]?.band || "",
        pick: selectedCosts?.[0]?.pick || "",
        supply_pallet: selectedCosts?.[0]?.supply_pallet || "",
        twenty_ft_handball: selectedCosts?.[0]?.twenty_ft_handball || "",
        forty_ft_handball: selectedCosts?.[0]?.forty_ft_handball || "",
        ...pallettrackPriceState(selectedPalletCosts),
    });

    const validate = (name, value) => {
        switch (name) {
            case "company":
                if (value.length > 20) return "Must be < 20 characters";
                break;
            case "address":
                if (!value) return "Address is required";
                break;
            case "postcode":
                if (!value) return "Postcode is required";
                break;
            case "phone":
                if (!/^\+?[0-9]{10,15}$/.test(value)) return "Invalid phone number";
                break;
            case "email":
                if (!/\S+@\S+\.\S+/.test(value)) return "Invalid email";
                break;
            case "account_ref":
                if (value.length > 8) return "Max 8 chars (e.g. TM000001)";
                break;
            default:
                break;
        }
        // Validate cost fields
        const cost = String(value).trim()
        if (((costFields.includes(name) || name === "pallet_price") && cost) && !moneyRegex.test(cost)) {
            return "Format 0.00 (e.g. 123.45)";
        }
        return null;
    };

    useEffect(() => {
        if (isOpen) {
            setFormData({
                id: customer.id || "",
                company: customer.company || "",
                address: customer.address || "",
                postcode: customer.postcode || "",
                email: customer.email || "",
                phone: customer.phone || "",
                account_ref: customer.account_ref || "",
                payment_terms: customer.payment_terms || "monthly",
                has_storage: Array.isArray(selectedCosts) && selectedCosts.length > 0,
                has_pallet_network: Boolean(selectedPalletCosts),
                weekly_pp: selectedCosts?.[0]?.weekly_pp || "",
                handling: selectedCosts?.[0]?.handling || "",
                use_rhd: selectedCosts?.[0]?.use_rhd || false,
                rhd: selectedCosts?.[0]?.rhd || "",
                wrap: selectedCosts?.[0]?.wrap || "",
                band: selectedCosts?.[0]?.band || "",
                pick: selectedCosts?.[0]?.pick || "",
                supply_pallet: selectedCosts?.[0]?.supply_pallet || "",
                twenty_ft_handball: selectedCosts?.[0]?.twenty_ft_handball || "",
                forty_ft_handball: selectedCosts?.[0]?.forty_ft_handball || "",
                fuel_surcharge: customer.fuel_surcharge || null,
                ...pallettrackPriceState(selectedPalletCosts),
            });
            setErrors({});
        }
    }, [isOpen, customer, selectedCosts, selectedPalletCosts]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        const errMsg = validate(name, value);
        setErrors((prev) => ({ ...prev, [name]: errMsg }));
    };

    const handleMoneyBlur = (e) => {
        const { name, value } = e.target;
        if (value !== "" && !Number.isNaN(Number(value))) {
            const formatted = Number(value).toFixed(2);
            setFormData((prev) => ({ ...prev, [name]: formatted }));
            setErrors((prev) => ({ ...prev, [name]: validate(name, formatted) }));
        }
    };

    const updatePallettrackPrice = (group, code, value) => {
        setFormData((prev) => ({
            ...prev,
            [group]: { ...prev[group], [code]: value },
        }));
    };

    const formatPallettrackPrice = (group, code, value) => {
        if (value !== "" && !Number.isNaN(Number(value))) {
            updatePallettrackPrice(group, code, Number(value).toFixed(2));
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};
        Object.entries(formData).forEach(([key, value]) => {
            const msg = validate(key, value);
            if (msg) newErrors[key] = msg;
        });

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const didSave = await onSubmit(formData);
        if (didSave === true) onClose();
    };

    if (!isOpen) return null;

    const inputStyle = {
        backgroundColor: theme === "light" ? "#fff" : "#444",
        color: theme === "light" ? "#757575" : "#909090",
        cursor: "not-allowed",
    };

    return (
        <div id={theme} className="overlay">
            <div className="modal-container customer-edit-modal">
                <h2 className="customer-edit-title">Edit Account</h2>

                <form onSubmit={(event) => event.preventDefault()}>
                    <section className="customer-edit-section">
                        <h3>Account Details</h3>
                        <div className="form two-column">
                            {[
                                { label: "Company", name: "company", disabled: true },
                                { label: "Address", name: "address" },
                                { label: "Postcode", name: "postcode" },
                                { label: "Phone Number", name: "phone" },
                                { label: "E-Mail", name: "email" },
                            ].map(({ label, name, disabled }) => (
                            <div className="input-group" key={name}>
                                <div className="label-group">
                                    <label>{label}:</label>
                                    {errors[name] && (
                                        <span className="error-text">{errors[name]}</span>
                                    )}
                                </div>
                                <input
                                    type={name === "email" ? "email" : "text"}
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleInputChange}
                                    className="input"
                                    disabled={disabled}
                                    style={disabled ? inputStyle : undefined}
                                />
                            </div>
                            ))}
                        </div>
                    </section>

                    <section className="customer-edit-section">
                        <h3>Billing</h3>
                        <div className="form two-column">
                            <div className="input-group">
                                <label>Account Ref:</label>
                                <input name="account_ref" value={formData.account_ref} onChange={handleInputChange} className="input" />
                                {errors.account_ref && <span className="error-text">{errors.account_ref}</span>}
                            </div>
                            <div className="input-group">
                                <label>Payment Terms:</label>
                                <select name="payment_terms" value={formData.payment_terms} onChange={handleInputChange} className="input">
                                    <option value="one_week">1 week</option>
                                    <option value="two_weeks">2 weeks</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="sixty_days">60 days</option>
                                    <option value="ninety_days">90 days</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Fuel Surcharge:</label>
                                <select name="fuel_surcharge" value={formData.fuel_surcharge || ''} onChange={handleInputChange} className="input">
                                    <option value="">No Fuel Surcharge</option>
                                    <option value="1">Low Surcharge</option>
                                    <option value="2">Medium Surcharge</option>
                                    <option value="3">High Surcharge</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    {warehouseEnabled && (
                        <section className="customer-edit-section">
                            <div className="customer-edit-section-header">
                                <h3>Warehouse</h3>
                                <label className="service-toggle">
                                    <input type="checkbox" checked={formData.has_storage} onChange={(e) => setFormData((prev) => ({ ...prev, has_storage: e.target.checked }))} />
                                    Enabled
                                </label>
                            </div>
                            {formData.has_storage && <>
                                <div className="warehouse-pricing-mode">
                                    <span>Handling pricing:</span>
                                    <label><input type="radio" checked={!formData.use_rhd} onChange={() => setFormData((prev) => ({ ...prev, use_rhd: false }))} /> In & out</label>
                                    <label><input type="radio" checked={formData.use_rhd} onChange={() => setFormData((prev) => ({ ...prev, use_rhd: true }))} /> RH&D inbound</label>
                                </div>
                                <div className="form two-column">
                                    {[
                                        { label: "Storage Per Pallet", name: "weekly_pp" },
                                        { label: formData.use_rhd ? "RH&D Per Inbound Pallet" : "Handling Per Movement", name: formData.use_rhd ? "rhd" : "handling" },
                                        { label: "Wrap", name: "wrap" }, { label: "Band", name: "band" },
                                        { label: "Pick", name: "pick" }, { label: "Supply Pallet", name: "supply_pallet" },
                                        { label: "20ft Handball", name: "twenty_ft_handball" }, { label: "40ft Handball", name: "forty_ft_handball" },
                                    ].map(({ label, name }) => (
                                        <div className="input-group" key={name}>
                                            <label>{label}:</label>
                                            <div className="money-input">
                                                <span>£</span>
                                                <input name={name} value={formData[name]} onChange={handleInputChange} onBlur={handleMoneyBlur} className="input" inputMode="decimal" />
                                            </div>
                                            {errors[name] && <span className="error-text">{errors[name]}</span>}
                                        </div>
                                    ))}
                                </div>
                            </>}
                        </section>
                    )}

                    <section className="customer-edit-section">
                        <div className="customer-edit-section-header">
                            <h3>Pallet Network</h3>
                            <label className="service-toggle">
                                <input type="checkbox" checked={formData.has_pallet_network} onChange={(e) => setFormData((prev) => ({ ...prev, has_pallet_network: e.target.checked }))} />
                                Enabled
                            </label>
                        </div>
                        {formData.has_pallet_network && (
                            <>
                            <div className="form two-column pallet-price-grid">
                                <div className="input-group">
                                    <label>Pallet price increment:</label>
                                    <div className="money-input">
                                        <span>£</span>
                                        <input type="text" inputMode="decimal" name="pallet_price" value={formData.pallet_price} onChange={handleInputChange} onBlur={handleMoneyBlur} className="input" />
                                    </div>
                                    {errors.pallet_price && <span className="error-text">{errors.pallet_price}</span>}
                                </div>
                            </div>
                            <h4 className="pallet-charge-heading">Pallet-Track service charges</h4>
                            <div className="form two-column pallet-price-grid">
                                {pallettrackServiceFields.map(({ label, code }) => (
                                    <div className="input-group" key={code}>
                                        <label>{label} ({code}):</label>
                                        <div className="money-input">
                                            <span>£</span>
                                            <input type="text" inputMode="decimal" value={formData.pallettrack_service_prices?.[code] || ""} onChange={(event) => updatePallettrackPrice("pallettrack_service_prices", code, event.target.value)} onBlur={(event) => formatPallettrackPrice("pallettrack_service_prices", code, event.target.value)} className="input" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <h4 className="pallet-charge-heading">Pallet-Track surcharge charges</h4>
                            <div className="form two-column pallet-price-grid">
                                {pallettrackSurchargeFields.map(({ label, code }) => (
                                    <div className="input-group" key={code}>
                                        <label>{label} ({code}):</label>
                                        <div className="money-input">
                                            <span>£</span>
                                            <input type="text" inputMode="decimal" value={formData.pallettrack_surcharge_prices?.[code] || ""} onChange={(event) => updatePallettrackPrice("pallettrack_surcharge_prices", code, event.target.value)} onBlur={(event) => formatPallettrackPrice("pallettrack_surcharge_prices", code, event.target.value)} className="input" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>
                        )}
                    </section>
                </form>

                <div className="button-group">
                    <button type="button" onClick={handleSubmit} className="submit-button">Update</button>
                    <button type="button" onClick={onClose} className="close-button">Close</button>
                </div>
            </div>
        </div>
    );
};

export default CustomerEditModal;
